// src/encounter/lib/to-plan-order.ts
// Map a DS MedicationResult (from MedicationSearchWidget) → a DS PlanOrder attached to a
// diagnosis on the AssessmentPlanCanvas. The caller supplies the order id (unique per
// attachment — the same medication can legitimately be ordered against more than one Dx,
// so the id is NOT derived from the medication id, which would collide).
// v1 label = name (+ strength); structured dosage is a follow-up (PROD-1165 deferral).
import type { MedicationResult, PlanOrder } from '@scripsteam/scrips-signal-ds';

export function medicationResultToPlanOrder(m: MedicationResult, orderId: string): PlanOrder {
  const label = m.strength ? `${m.name} ${m.strength}` : m.name;
  // dxIds empty — the host attaches the drop-target/tapped Dx when it appends the order (v2 PROD-1422).
  return { id: orderId, type: 'medication', label, dxIds: [] };
}

/**
 * Replace an existing order IN PLACE with an edited one (PROD-1433 click-to-edit): adopt the edited
 * order's fields (label/type) but KEEP the original identity — the same `id` (so the side-map payload
 * key + any server id survive). The edit dialog builds a fresh PlanOrder with a derived id + empty
 * dxIds; this stamps the original id back.
 *
 * Links (PROD-2886): `linkedDxIds` is the edit form's DIAGNOSIS (n) * selection. When it holds at
 * least one id, the order is re-linked to exactly that set (deduped, in the order given — the
 * multi-select emits canvas order, so `dxIds[0]` stays the nesting parent), the same write the
 * "Change diagnosis links" editor makes and Flutter's updateItemDiagnosis makes on save. Absent or
 * empty (a form with no picker, or the min-1 rule would be broken) → the original links are kept.
 * The payload is keyed by the order id, so every nested copy the save fans out shares the one
 * payload and its carried server dates (PROD-2854). Returns a new array; unmatched orders pass
 * through unchanged.
 */
export function replaceOrderKeepingLinks(
  orders: PlanOrder[],
  orderId: string,
  edited: PlanOrder,
  linkedDxIds?: readonly string[],
): PlanOrder[] {
  const linked = linkedDxIds ? [...new Set(linkedDxIds)].filter(Boolean) : [];
  return orders.map((o) =>
    o.id === orderId ? { ...edited, id: orderId, dxIds: linked.length > 0 ? linked : o.dxIds } : o,
  );
}
